import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl } from "../lib/api";
import { MessageCircle, Phone, Menu, Zap } from "lucide-react";
import { MessagesSheet } from "./MessagesSheet";
import { CallHistorySheet } from "./CallHistorySheet";
import { PromoInviteSheet } from "./PromoInviteSheet";

export function AppHeader({ title }) {
  const { user, family } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [callsOpen, setCallsOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoActive, setPromoActive] = useState(false);
  const [badge, setBadge] = useState(0);
  const navigate = useNavigate();

  const canModerate = family?.role === "admin" || family?.role === "organizer";
  const multi = user?.family_count > 1 && family?.name;
  const possessive = (n) => (/s$/i.test(n) ? `${n}'` : `${n}'s`);
  let displayTitle = title;
  if (multi) {
    if (title === "The Vault" || title === "Vault") displayTitle = `${possessive(family.name)} Vault`;
    else displayTitle = `${family.name} ${title}`;
  }

  useEffect(() => {
    let alive = true;
    api.get("/promotions/active")
      .then(({ data }) => { if (alive) setPromoActive(!!data.active); })
      .catch(() => { if (alive) setPromoActive(false); });
    return () => { alive = false; };
  }, [family?.id]);

  const loadBadge = useCallback(async () => {
    try {
      const [u, n] = await Promise.all([
        api.get("/messages/unread-count").catch(() => ({ data: { count: 0 } })),
        api.get("/notifications").catch(() => ({ data: [] })),
      ]);
      const unreadNotifs = (n.data || []).filter((x) => x.read !== true).length;
      setBadge((u.data.count || 0) + unreadNotifs);
    } catch { setBadge(0); }
  }, []);

  useEffect(() => {
    loadBadge();
    const t = setInterval(loadBadge, 8000);
    const h = () => loadBadge();
    window.addEventListener("fv-refresh-badge", h);
    return () => { clearInterval(t); window.removeEventListener("fv-refresh-badge", h); };
  }, [loadBadge]);

  return (
    <>
      <div className="app-topbar sticky top-0 z-40 glass hairline px-5 pt-6 pb-4 lg:px-8 lg:pt-8 lg:pb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => window.dispatchEvent(new Event("fv:toggle-rail"))} data-testid="rail-toggle-btn"
              className="fv-rail-toggle h-10 w-10 rounded-full grid place-items-center shrink-0"
              style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }} aria-label="Open menu">
              <Menu size={19} style={{ color: "var(--text-secondary)" }} />
            </button>
            <h1 className="font-serif-display text-3xl lg:text-4xl leading-tight truncate" style={{ color: "var(--text-primary)" }} data-testid="app-header-title">
              {displayTitle}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {promoActive && (
              <button onClick={() => setPromoOpen(true)} data-testid="promo-jewel-btn"
                className="promo-zap relative h-11 w-11 rounded-full grid place-items-center"
                style={{ background: "var(--bg-elevated)", border: "1px solid color-mix(in srgb, var(--accent) 45%, var(--border))" }}
                aria-label="Invite friends and earn credits">
                <Zap size={18} className="gold-text" />
              </button>
            )}
            <button onClick={() => setCallsOpen(true)} data-testid="calls-btn"
              className="h-11 w-11 rounded-full grid place-items-center"
              style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
              <Phone size={17} style={{ color: "var(--text-secondary)" }} />
            </button>
            <button onClick={() => setSheetOpen(true)} data-testid="messages-btn"
              className="relative h-11 w-11 rounded-full grid place-items-center"
              style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
              <MessageCircle size={18} style={{ color: "var(--text-secondary)" }} />
              {badge > 0 && (
                <span data-testid="messages-badge"
                  className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full grid place-items-center text-[10px] font-bold"
                  style={{ background: "var(--accent)", color: "#fff" }}>
                  {badge}
                </span>
              )}
            </button>
            <button onClick={() => navigate("/profile")} data-testid="profile-menu-btn"
              className="h-11 w-11 rounded-full grid place-items-center gold-border overflow-hidden"
              style={{ background: "var(--bg-elevated)" }}>
              {user?.photo ? (
                <img src={fileUrl(user.photo)} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="font-serif-display text-lg gold-text">
                  {(user?.name || "?").charAt(0).toUpperCase()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
      <MessagesSheet open={sheetOpen} onClose={() => setSheetOpen(false)} onChange={loadBadge} canModerate={canModerate} />
      <CallHistorySheet open={callsOpen} onClose={() => setCallsOpen(false)} />
      <PromoInviteSheet open={promoOpen} onClose={() => setPromoOpen(false)} />
    </>
  );
}
